/**
 * AI-assisted dataset semantics — client for the streamed
 * `POST /datasets/{id}/semantics/propose-stream` endpoint (ADR-0026, L2).
 *
 * The per-dataset counterpart to `lib/relationshipsAi`: same SSE-over-POST shape
 * (request body + admin bearer token, so `EventSource` can't be used — we read
 * the response body stream by hand). The frame parser (`decodeSemanticsFrames`)
 * is split out pure so the chunk-boundary handling is unit-tested without a live
 * network.
 *
 * Each event drives the **debugger**: `step`/`context` (what's being fed),
 * `thinking`/`delta` (the model's live output), `proposal` (the parsed descriptor
 * + token usage), `error`.
 */
import { useAuthStore } from "./auth";
import type { DatasetSemanticsInput } from "./api-dataset-semantics";
import { consumeSse, parseSseFrames } from "./sseStream";

export type SemanticsAiEvent =
  | { type: "step"; key: string; label: string }
  | {
      type: "context";
      dataset: string;
      field_count: number;
      row_count: number;
      note?: string;
    }
  | { type: "thinking"; text: string }
  | { type: "delta"; text: string }
  | {
      type: "proposal";
      proposal: DatasetSemanticsInput;
      usage: { input_tokens?: number; output_tokens?: number; cache_read_input_tokens?: number };
    }
  | { type: "error"; message: string }
  | { type: "done" };

/**
 * Pure SSE frame decoder: given the carry-over buffer + a freshly-read chunk,
 * return the parsed events for every COMPLETE frame and the remaining buffer (an
 * incomplete trailing frame). Frames are `\n\n`-separated; `data:` lines are
 * JSON. Malformed JSON in a frame is skipped, not thrown.
 */
export function decodeSemanticsFrames(
  buffer: string,
  chunk: string,
): { events: SemanticsAiEvent[]; buffer: string } {
  return parseSseFrames<SemanticsAiEvent>(buffer, chunk);
}

/**
 * POST the proposal request and invoke `onEvent` for each SSE event until the
 * stream ends. Resolves when the stream closes; rejects only on a network error
 * (an HTTP error surfaces as an `error` event, then resolves).
 */
export async function streamProposeSemantics(
  datasetId: string,
  opts: { thinking?: boolean },
  onEvent: (ev: SemanticsAiEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`/api/admin/datasets/${datasetId}/semantics/propose-stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ thinking: !!opts.thinking }),
    signal,
  });
  if (!res.ok || !res.body) {
    // A 422 (AI unconfigured) carries a JSON body, not an SSE stream.
    let message = `http_${res.status}`;
    try {
      const j = (await res.json()) as { detail?: string; message?: string };
      message = j.detail || j.message || message;
    } catch {
      /* keep the http_<status> fallback */
    }
    onEvent({ type: "error", message });
    return;
  }
  await consumeSse<SemanticsAiEvent>(res.body, onEvent);
}
